<template>
  <div class="text-message" :class="[msg.role || msg.type, { 'block-message': msg.isBlock }]">
    <div class="avatar" :class="msg.role || msg.type">
      <img v-if="avatarImage" :src="avatarImage" class="avatar-img" />
      <van-icon v-else :name="avatarIcon" />
    </div>
    <div class="message-body">
      <div class="sender-name" v-if="showName">{{ msg.agentName }}</div>
      <div class="bubble">
        <div class="content">
          <template v-for="(seg, idx) in splitByPlate(msg.content)">
            <span
              v-if="seg.isPlate"
              :key="'p' + idx"
              style="color:#ee0a24;font-weight:bold;font-size:18px;white-space:pre-line;display:inline;"
            >{{ seg.text }}</span>
            <span
              v-else
              :key="'n' + idx"
              style="white-space:pre-line;display:inline;"
            >{{ seg.text }}</span>
          </template>
        </div>
        <div class="example-image" v-if="msg.exampleImage" @click="preview(msg.exampleImage)">
          <div class="example-label">示例图</div>
          <img :src="msg.exampleImage" />
        </div>
        <div class="quick-replies" v-if="msg.quickReplies && msg.quickReplies.length">
          <van-button
            v-for="(reply, idx) in msg.quickReplies"
            :key="idx"
            size="small"
            hairline
            class="quick-reply-btn"
            @click="onQuickReply(reply)"
          >{{ reply }}</van-button>
        </div>
        <div class="buttons" v-if="msg.buttons && msg.buttons.length">
          <van-button
            v-for="(btn, idx) in msg.buttons"
            :key="idx"
            size="small"
            type="primary"
            class="action-btn"
            @click="onButton(btn)"
          >{{ btn.text }}</van-button>
        </div>
        <div class="bubble-footer" v-if="msg.time" :class="{ 'user': msg.type === 'user' }">
          <div class="time">{{ msg.time }}</div>
        </div>
      </div>
      <div class="progress" v-if="msg.progress">{{ msg.progress }}</div>
    </div>
  </div>
</template>

<script>
import plateHighlightMixin from './mixins/plateHighlight';

const plateRegex = /[京津沪渝冀豫云辽黑湘皖鲁新苏浙赣鄂桂甘晋蒙陕吉闽贵粤青藏川宁琼][A-Z][A-Z0-9]{3,6}[挂学警港澳]?/g;

export default {
  name: 'TextMessage',
  mixins: [plateHighlightMixin],
  props: {
    msg: { type: Object, required: true },
    index: { type: Number, default: 0 },
  },
  computed: {
    avatarIcon() {
      if (this.msg.type === 'user' || this.msg.role === 'user') return 'user-o';
      return 'service-o';
    },
    avatarImage() {
      if (this.msg.type === 'system' || this.msg.role === 'system') return require('@/assets/images/xitong.png');
      if (this.msg.type === 'user' || this.msg.role === 'user') return require('@/assets/images/sijiuyonghu.png');
      return require('@/assets/images/robot.png');
    },
    showName() {
      return this.msg.type !== 'user' && this.msg.role !== 'user' && this.msg.agentName;
    },
    contentParts() {
      const content = String(this.msg.content || '');
      const parts = [];
      let lastIndex = 0;
      // 关键：用 matchAll 避免 module-level plateRegex 的 lastIndex 跨实例累积，
      //   导致同一个正则实例在多个 TextMessage 组件间互相干扰，漏掉车牌匹配
      const matches = content.matchAll(plateRegex);
      for (const match of matches) {
        if (match.index > lastIndex) {
          parts.push({ text: content.slice(lastIndex, match.index), isPlate: false });
        }
        parts.push({ text: match[0], isPlate: true });
        lastIndex = match.index + match[0].length;
      }
      if (lastIndex < content.length) {
        parts.push({ text: content.slice(lastIndex), isPlate: false });
      }
      return parts.length ? parts : [{ text: content, isPlate: false }];
    },
  },
  methods: {
    // 拆分消息内容为带"是否是车牌号"标记的片段，供 template 内联 style 渲染。
    // splitByPlate 已从 mixin plateHighlight 注入，这里只保留其他自定义方法。
    getPartStyle(part) {
      const base = { whiteSpace: 'pre-line' };
      if (part && part.isPlate) {
        return {
          ...base,
          color: '#000',
          fontWeight: 'bold',
          fontSize: '28px',
          padding: '2px 6px',
          background: '#fff3a0',  // 调试：浅黄色背景，验证 plateRegex 是否匹配到了
          borderRadius: '4px',
        };
      }
      return base;
    },
    preview(url) {
      this.$emit('preview', url);
    },
    onQuickReply(reply) {
      this.$emit('choice', { text: reply, value: reply });
    },
    onButton(btn) {
      this.$emit('choice', { text: btn.text, value: btn.value || btn.text, action: btn.action });
    },
  },
};
</script>

<style lang="scss" scoped>
.text-message {
  display: flex;
  align-items: flex-start;
  margin-bottom: 12px;
  width: 100%;
}
.text-message.user {
  flex-direction: row-reverse;
}
.avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  color: #fff;
  flex-shrink: 0;
}
.avatar.robot { background: #e8f8ff; }
.avatar.user { background: #1989fa; }
.avatar.system { background: #f5f5f5; }
.avatar-img {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  object-fit: cover;
}
.message-body {
  display: flex;
  flex-direction: column;
  flex: 0 1 auto;
  min-width: 0;
  margin: 0 12px;
  align-items: flex-start;  /* 整体靠左对齐（贴左侧头像） */
}
.text-message.user .message-body {
  align-items: flex-end;   /* 用户消息靠右 */
}
.text-message:not(.user) .message-body {
  align-items: flex-start;  /* 机器人/系统靠左 */
}
/* 气泡本身：内部子元素全部靠右 */
.bubble {
  display: inline-block;   /* 气泡按内容收缩 */
  max-width: 100%;
  padding: 10px 12px;
  margin: 0;
  border-radius: 12px;
  background: #fff;
  font-size: 14px;
  line-height: 1.5;
  color: #333;
  word-break: break-word;
  text-align: left;
}
.bubble .content,
.bubble .example-image,
.bubble .quick-replies,
.bubble .buttons {
  text-align: left;
}
.bubble .example-image {
  margin-top: 8px;
  img {
    max-width: 100%;
    max-height: 160px;
    border-radius: 8px;
    display: block;
  }
}
.text-message.user .bubble {
  background: #fff;
  color: #333;
}
.text-message.system .bubble {
  background: #fff;
  color: #333;
}
.block-message .bubble {
  border: 1px solid #ee0a24;
  background: #ffe5e5;
  color: #ee0a24 !important;
}
.block-message .bubble .content,
.block-message .bubble .content span {
  color: #ee0a24;
}
.sender-name {
  font-size: 12px;
  color: #999;
  margin-bottom: 4px;
  margin-left: 0;
}
.progress {
  font-size: 12px;
  color: #999;
  margin-top: 4px;
  margin-left: 0;
}
.bubble-footer {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 8px;
  min-height: 18px;
}
.bubble-footer.user {
  justify-content: flex-start;
}
.bubble-footer .time {
  font-size: 12px;
  color: #999;
}
.plate-highlight {
  color: #000 !important;
  font-weight: bold !important;
  font-size: 28px !important;
}
.example-image {
  margin-top: 8px;
  cursor: pointer;
  .example-label {
    font-size: 14px;
    color: #1989fa;
    margin-bottom: 6px;
    line-height: 1.4;
  }
  img {
    max-width: 100%;
    max-height: 160px;
    border-radius: 8px;
    display: block;
  }
}
.quick-replies, .buttons {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 10px;
}
.quick-reply-btn {
  border-radius: 14px;
}
</style>
